import { datadogRum } from "@datadog/browser-rum";
import { Builder } from "@builder.io/react";
import {
  initRum,
  initialTrackingConsent,
  isConsentManagerOnPage,
  observeDocumentTitle,
  syncTrackingConsentWithOsano,
} from "@ph/ps-trackers";
import type { NextRouter } from "next/router";
import { useRouter } from "next/router";
import { useEffect } from "react";

/**
 * RUM must stay off when there are no credentials, or when the page is being
 * rendered inside the Builder.io editor / preview iframe.
 */
function shouldInitDatadogRum(): boolean {
  if (
    !process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID ||
    !process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN
  ) {
    return false;
  }
  return !(Builder.isEditing || Builder.isPreviewing);
}

/** Initializes the SDK once per page load; a second call is a no-op. */
function initDatadogRum() {
  initRum(datadogRum, {
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
    site: process.env.NEXT_PUBLIC_DATADOG_SITE,
    service: "dtc-v1",
    env: process.env.NEXT_PUBLIC_DATADOG_ENV,
    version: process.env.NEXT_PUBLIC_DATADOG_VERSION,
    sessionReplaySampleRate: 0,
    defaultPrivacyLevel: "mask-user-input",
    enablePrivacyForActionName: true,
    trackingConsent: initialTrackingConsent(isConsentManagerOnPage()),
    // Views are tracked automatically from history.pushState, so view.url keeps
    // agent_id / gclid / utm_* query params. Do not set trackViewsManually.
  });
}

/**
 * Records `document.title` as the `title` view context property so 404 / error
 * views can be filtered out, and keeps it current. Returns the cleanup.
 */
function trackViewTitle(events: NextRouter["events"]) {
  const title = observeDocumentTitle((value) =>
    datadogRum.setViewContextProperty("title", value)
  );

  // A new view whose title did not change still needs the attribute.
  events.on("routeChangeComplete", title.refresh);

  return () => {
    title.disconnect();
    events.off("routeChangeComplete", title.refresh);
  };
}

/**
 * Datadog RUM for the Builder.io landing pages (PPI-2003). Renders nothing.
 *
 * Gated on credentials rather than NEXT_PUBLIC_ENABLE_THIRD_PARTY_SCRIPTS:
 * RUM is first-party monitoring and must keep reporting even if that flag is
 * used as a kill switch for marketing tags.
 *
 * The SDK is imported statically on purpose. Osano blocks scripts inserted at
 * runtime during the initial load, and on enroll.pivothealth.com that included
 * the chunks of a dynamic `import("@datadog/browser-rum")` (seen Nov 2026), so
 * the SDK has to travel in the page's own `<script>` tags, which Osano leaves
 * alone. Consent is still honored: with Osano on the page the SDK starts with
 * `trackingConsent: "not-granted"` and sends nothing until Osano reports the
 * Analytics category accepted.
 */
export default function DatadogRum() {
  const router = useRouter();

  useEffect(() => {
    if (!shouldInitDatadogRum()) return;

    initDatadogRum();
    const stopTrackingTitle = trackViewTitle(router.events);
    const consent = isConsentManagerOnPage()
      ? syncTrackingConsentWithOsano(datadogRum)
      : undefined;

    return () => {
      consent?.disconnect();
      stopTrackingTitle();
    };
  }, [router.events]);

  return null;
}
